// @ts-nocheck
import React, { useMemo, useState } from "react";
import { LoadingOverlay } from "@insite/mobius/index";
import { css } from "styled-components";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import translate from "@insite/client-framework/Translate";
import {
    EditableInvoicePaymentDto,
    EditableInvoicePaymentOption,
    InvoicePaymentOption,
} from "@store/Framework/Store/Pages/AccountBalance/AccountBalanceReducer";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Typography, { TypographyPresentationProps } from "@store/MobiusOverrides/Typography/Typography";
import {
    attributesStyles,
    ProductDetailsAttributesStyles,
} from "@store/Overrides/Widgets/ProductDetails/ProductDetailsAttributes";
import useLocalizeCurrency from "@store/Framework/Common/Hooks/useLocalizeCurrency";
import useGetShipToAndBillTo from "@store/Framework/Store/Data/Accounts/Hooks/useGetShipToAndBillTo";
import { CustomWidgetModule } from "@store/Framework/Types/types";
import InfosBox from "@store/Library/Components/Toolkit/InfosBox";
import getColor from "@insite/mobius/utilities/getColor";
import { ButtonIcon, ButtonPresentationProps } from "@store/MobiusOverrides/Button/Props";

import Button from "@store/MobiusOverrides/Button/Button";
import InfoLine from "@toolkit/Library/Components/Icons/Info/InfoLine";
import AccountBalanceCreditsModal from "./AccountBalanceCreditsModal";
import { extractStylesToArray, isSafe } from "@insite/client-framework/Common/Utilities/isSafeStyles";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import useAccountBalanceState, { useIsAccountBalanceLoading } from "@store/Framework/Common/Hooks/AccountBalance/useAccountBalanceState";

enum fields {
    aboveContent = "aboveContent",
    belowContent = "belowContent",
    visibleFields = "visibleFields",
}

enum displayableFields {
    customerName = "customerName",
    terms = "terms",
    balance = "balance",
    credits = "credits",
    overdue = "overdue",
    current = "current",
    lastPaymentAmount = "lastPaymentAmount",
    lastInvoiceDate = "lastInvoiceDate",
    lastPayDate = "lastPayDate",
}

export type DefaultAccountBalance = Omit<EditableInvoicePaymentDto, "paymentOptions">;
const defaultAccountBalance: DefaultAccountBalance = {
    termsDescription: "",
    balance: 0,
    overdue: 0,
    current: 0,
    lastPaymentAmount: 0,
    lastInvoiceDate: new Date(),
    lastPayDate: new Date(),
    payments: [] as EditableInvoicePaymentOption[],
    credits: [] as InvoicePaymentOption[],
    canPayOnline: false,
    invoicePaymentEnabled: true,
    accountDepositEnabled: true,
    accountDepositEnabledUntil: new Date(),
    accountDepositMaximum: 0,
};

interface AccountCardStyles extends ProductDetailsAttributesStyles {
    cardWrapper?: InjectableCss;
    widgetWrapper?: InjectableCss;
    modelButton?: ButtonPresentationProps;
    creditTypography?: TypographyPresentationProps;
    creditButton?: ButtonPresentationProps;
}

const accountCardStyles: AccountCardStyles = {
    ...attributesStyles,
    creditButton: {
        variant: "tertiary",
        sizeVariant: "small",
        typographyProps: {
            css: css`
                font-weight: normal;
            `,
        },
        css: css`
            outline: none;
            padding: 0;
            border: none;
            min-height: auto;
            font-weight: normal;

            outline-offset: 8px;
            :focus-visible {
                outline-offset: 8px;
            }
        `,
    },
    cardWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            box-shadow: ${({ theme }) => theme.shadows["small"]};
            padding: ${pxToRem(20)};
            border-radius: ${pxToRem(10)};
            gap: ${pxToRem(10)};
        `,
    },
    widgetWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            gap: ${pxToRem(32)};
        `,
    },
    item: {
        css: css`
            ${attributesStyles.item?.css}
            border-bottom: 1px ridge ${getColor("common.border")};
        `,
    },
    modelButton: {
        mergeCss: true,
        css: css`
            color: ${getColor("primary")};
            min-height: auto;
            border: none;
            background: transparent;
            position: relative;
            :hover:not(:disabled) {
                background: transparent;
            }
            min-width: auto;
            font-weight: ${({ theme }) => theme.design.typography.weight.light};
            border-radius: ${pxToRem(12)};
            padding: 0;
            margin-left: 1px;
            transition: color 0.2s ease-in-out;
            ${ButtonIcon} {
                border-radius: 50%;

                margin-right: 0;
                :hover > svg {
                    color: ${getColor("primary.dark")};
                }
                width: ${pxToRem(16)};
                height: ${pxToRem(16)};
                > svg {
                    height: ${pxToRem(16)};
                    color: ${getColor("primary")};
                    width: ${pxToRem(16)};
                }
            }
        `,
        icon: {
            position: "left",
            src: InfoLine,
        },

        typographyProps: {
            italic: true,
            css: css`
                font-size: ${({ theme }) => theme.design.typography.size.smallest};
            `,
        },
    },
    creditTypography: {
        ...attributesStyles.attributeTypeLabelText,
        mergeCss: true,
        css: css`
            ${attributesStyles.attributeTypeLabelText?.css}
            display: flex;
        `,
    },
};
interface AccountBalanceCardProps extends WidgetProps {
    fields: {
        [fields.aboveContent]: string;
        [fields.belowContent]: string;
        [fields.visibleFields]: HasFields[];
    };
}
const AccountBalanceCard: React.FC<AccountBalanceCardProps> = ({
    fields: { aboveContent, belowContent, visibleFields },
}) => {
    const styles = accountCardStyles;
    const isLoading = useIsAccountBalanceLoading();

    const { accountBalance = defaultAccountBalance } = useAccountBalanceState() || {};

    const [isCreditsModalOpen, setIsCreditsModalOpen] = useState(false);

    const hasCredits = accountBalance.credits && accountBalance.credits.length > 0;
    const totalCredits = accountBalance.credits?.reduce((acc, credit) => acc + credit.balanceDue, 0) ?? 0;

    const currencyFormatter = useLocalizeCurrency();
    const customerName = useGetShipToAndBillTo()?.billTo?.companyName;

    const handleOpenCreditsModal = () => {
        if (hasCredits) {
            setIsCreditsModalOpen(true);
        }
    };

    const handleCloseCreditsModal = () => {
        setIsCreditsModalOpen(false);
    };

    if (!accountBalance.invoicePaymentEnabled) {
        return (
            <InfosBox type="error" title={translate("Error")}>
                {translate("Invoice Payment is not enabled for this account.")}
            </InfosBox>
        );
    }

    const fieldRenderers: { [key in displayableFields]?: React.ReactNode } = useMemo(
        () => ({
            [displayableFields.customerName]: (
                <Typography as="li" {...styles.item} key={displayableFields.customerName}>
                    <Typography {...styles.attributeTypeLabelText}>{translate("Customer Name")}</Typography>
                    <Typography {...styles.attributeTypeValuesText}>{customerName}</Typography>
                </Typography>
            ),
            [displayableFields.terms]: (
                <Typography as="li" {...styles.item} key={displayableFields.terms}>
                    <Typography {...styles.attributeTypeLabelText}>{translate("Terms")}</Typography>
                    <Typography {...styles.attributeTypeValuesText}>
                        {translate(accountBalance.termsDescription)}
                    </Typography>
                </Typography>
            ),
            [displayableFields.balance]: (
                <Typography as="li" {...styles.item} key={displayableFields.balance}>
                    <Typography {...styles.attributeTypeLabelText}>{translate("Balance")}</Typography>
                    <Typography {...styles.attributeTypeValuesText}>
                        {currencyFormatter(accountBalance.balance)}
                    </Typography>
                </Typography>
            ),
            [displayableFields.credits]: (
                <Typography as="li" {...styles.item} key={displayableFields.credits}>
                    <Typography {...styles.creditTypography}>
                        {translate("Credits")}
                        {hasCredits && (
                            <Button
                                title={translate("Credits Info")}
                                {...styles.modelButton}
                                onClick={handleOpenCreditsModal}
                                disabled={!hasCredits}
                                data-test-selector="creditsInfoButton"
                            />
                        )}
                    </Typography>
                    <Typography {...styles.attributeTypeValuesText}>
                        {hasCredits ? (
                            <Button onClick={handleOpenCreditsModal} {...styles.creditButton}>
                                {currencyFormatter(totalCredits)}
                            </Button>
                        ) : (
                            <>{currencyFormatter(0)}</>
                        )}
                    </Typography>
                </Typography>
            ),
            [displayableFields.overdue]: (
                <Typography as="li" {...styles.item} key={displayableFields.overdue}>
                    <Typography {...styles.attributeTypeLabelText}>{translate("Overdue")}</Typography>
                    <Typography {...styles.attributeTypeValuesText}>
                        {currencyFormatter(accountBalance.overdue)}
                    </Typography>
                </Typography>
            ),
            [displayableFields.current]: (
                <Typography as="li" {...styles.item} key={displayableFields.current}>
                    <Typography {...styles.attributeTypeLabelText}>{translate("Current")}</Typography>
                    <Typography {...styles.attributeTypeValuesText}>
                        {currencyFormatter(accountBalance.current)}
                    </Typography>
                </Typography>
            ),
            [displayableFields.lastPaymentAmount]: (
                <Typography as="li" {...styles.item} key={displayableFields.lastPaymentAmount}>
                    <Typography {...styles.attributeTypeLabelText}>{translate("Last Payment Amount")}</Typography>
                    <Typography {...styles.attributeTypeValuesText}>
                        {currencyFormatter(accountBalance.lastPaymentAmount)}
                    </Typography>
                </Typography>
            ),
            [displayableFields.lastInvoiceDate]: (
                <Typography as="li" {...styles.item} key={displayableFields.lastInvoiceDate}>
                    <Typography {...styles.attributeTypeLabelText}>{translate("Last Invoice Date")}</Typography>
                    <Typography {...styles.attributeTypeValuesText}>
                        {new Date(accountBalance.lastInvoiceDate).toLocaleDateString()}
                    </Typography>
                </Typography>
            ),
            [displayableFields.lastPayDate]: (
                <Typography as="li" {...styles.item} key={displayableFields.lastPayDate}>
                    <Typography {...styles.attributeTypeLabelText}>{translate("Last Payment Date")}</Typography>
                    <Typography {...styles.attributeTypeValuesText}>
                        {new Date(accountBalance.lastPayDate).toLocaleDateString()}
                    </Typography>
                </Typography>
            ),
        }),
        [accountBalance, customerName, currencyFormatter, hasCredits, totalCredits, styles],
    );

    return (
        <>
            <LoadingOverlay loading={isLoading}>
                <StyledWrapper {...styles.widgetWrapper}>
                    <StyledWrapper {...styles.cardWrapper}>
                        <Typography as="ul" {...styles.list}>
                            {visibleFields
                                .filter(field => field.fields.visible !== false)
                                .map(field => fieldRenderers[field.fields.name as displayableFields])}
                        </Typography>
                    </StyledWrapper>
                </StyledWrapper>
            </LoadingOverlay>

            <AccountBalanceCreditsModal
                isOpen={isCreditsModalOpen}
                handleClose={handleCloseCreditsModal}
                aboveContent={aboveContent}
                belowContent={belowContent}
            />
        </>
    );
};

const widgetModule: CustomWidgetModule = {
    component: AccountBalanceCard,
    definition: {
        group: "Payments",
        displayName: "Account Balance Card",
        fieldDefinitions: [
            {
                name: fields.visibleFields,
                displayName: "Visible Fields",
                editorTemplate: "ListField",
                fieldType: "General",
                defaultValue: [
                    { fields: { name: displayableFields.customerName, displayName: "Customer Name", visible: true } },
                    { fields: { name: displayableFields.terms, displayName: "Terms", visible: true } },
                    { fields: { name: displayableFields.balance, displayName: "Balance", visible: true } },
                    { fields: { name: displayableFields.credits, displayName: "Credits", visible: true } },
                    { fields: { name: displayableFields.overdue, displayName: "Overdue", visible: true } },
                    { fields: { name: displayableFields.current, displayName: "Current", visible: true } },
                    {
                        fields: {
                            name: displayableFields.lastPaymentAmount,
                            displayName: "Last Payment Amount",
                            visible: true,
                        },
                    },
                    {
                        fields: {
                            name: displayableFields.lastInvoiceDate,
                            displayName: "Last Invoice Date",
                            visible: true,
                        },
                    },
                    {
                        fields: {
                            name: displayableFields.lastPayDate,
                            displayName: "Last Payment Date",
                            visible: true,
                        },
                    },
                ],
                getDisplay: (item: HasFields) => {
                    return (
                        (item.fields.displayName || item.fields.name) +
                        (item.fields.visible === false ? " (Hidden)" : "")
                    );
                },
                hideAdd: true,
                hideEdit: false,
                hideDelete: true,
                fieldDefinitions: [
                    {
                        name: "name",
                        editorTemplate: "TextField",
                        defaultValue: "",
                        isVisible: () => false,
                    },
                    {
                        name: "displayName",
                        editorTemplate: "TextField",
                        defaultValue: "",
                        isVisible: () => false,
                    },
                    {
                        name: "visible",
                        editorTemplate: "CheckboxField",
                        defaultValue: true,
                        displayName: "Visible",
                        isVisible: () => true,
                    },
                ],
            },
            {
                name: fields.aboveContent,
                displayName: "Modal Content",
                editorTemplate: "RichTextField",
                fieldType: "Translatable",
                isRequired: true,
                defaultValue: "",
                validate: (html: string) => {
                    const arrayOfStyles = extractStylesToArray(html);
                    return arrayOfStyles?.every(style => isSafe(style))
                        ? null
                        : "The HTML contains invalid styles that prevent it from being saved";
                },
            },
            {
                name: fields.belowContent,
                displayName: "Below Content",
                editorTemplate: "RichTextField",
                fieldType: "Translatable",
                isRequired: false,
                defaultValue: "",
                validate: (html: string) => {
                    const arrayOfStyles = extractStylesToArray(html);
                    return arrayOfStyles?.every(style => isSafe(style))
                        ? null
                        : "The HTML contains invalid styles that prevent it from being saved";
                },
            },
        ],
    },
};

export default widgetModule;
